/**
 * resolvePageMap — TPN-3 (Template Page Numbering, 2026-05-18).
 *
 * Pure, deterministic resolver that turns a list of TemplateSections
 * into a page-map (each section's startPage + endPage). The TOC and any
 * "starts on p.N" surface in the UI ALWAYS reads from this — never from
 * a stored `pageNumber` field. Page numbers are a DERIVED value.
 *
 * Three input sources, in priority order (per pack §Architecture):
 *
 *   1. measuredPages   (DOM-based, post-paint) — most accurate;
 *                       supplied by TPN-5 PagedJS / DOM measurement at
 *                       print time. Optional — omitted in screen mode.
 *   2. estimatedPages  (heuristic) — `section.estimatedPages` if
 *                       stamped; otherwise computed on the fly via
 *                       `estimateSectionPages`. The resolver accepts
 *                       an `estimator` callback so callers can inject
 *                       a content-aware estimator (live reports) or
 *                       use the default snapshot (Studio fixtures).
 *   3. pagePin         (manual) — only honoured when
 *                       `section.startMode === 'page-pin'`. Clamped to
 *                       `Math.max(pin, cursor)` so a pin can never
 *                       move a section BACK over the prior section's
 *                       footprint.
 *
 * Backward-compat invariants (per pack §Regression invariants):
 *
 *   - Sections with no `startMode` default to 'auto'.
 *   - Sections without `visible === false` are included; visible:false
 *     entries are dropped (matches the body's render filter).
 *   - Sections are sorted by `order` when present, otherwise by their
 *     input-array index (stable).
 *   - The resolver always returns ≥1 section in the map when ≥1 input
 *     section is visible (defensive against empty estimator output).
 */

import type { SectionConfig } from '../../types/reportTemplate';
import {
  DEFAULT_A4,
  estimateSectionPages,
  type EstimableSection,
  type EstimatorOptions,
} from './estimateSectionPages';

export type PageMapSource = 'pinned' | 'estimated' | 'measured';

/**
 * W4 (page-chrome format unification) — the page map owns page POSITIONS
 * (1-based `startPage`/`endPage`), NOT their rendered FORMAT. The
 * page-number FORMAT (`arabic` / `roman-front` / `page-of-total`) is a
 * presentation concern applied at the render boundary: the TOC formats each
 * `startPage` via `reportLayoutTokens.formatPageNumber(startPage,
 * presentation.pageNumberFormat)` (see `TableOfContents.tsx`), and the
 * puppeteer footer + Word fields read the SAME `pageNumberFormat` token. So
 * this resolver stays purely positional — keeping it format-agnostic means
 * a format switch never perturbs the cursor math (break positions are
 * identical across all three formats).
 */
export interface PageMapEntry {
  /** The section id (matches `SectionConfig.sectionId`). */
  id: string;
  /** 1-based page number this section starts on. Rendered via
   *  `reportLayoutTokens.formatPageNumber` at the TOC/footer boundary
   *  (W4); the raw value here is always a decimal position. */
  startPage: number;
  /** 1-based page number this section ends on. */
  endPage: number;
  /** Provenance of the page count used for this section. */
  source: PageMapSource;
  /** Whether the resolver had to clamp a pin to avoid overlap. UI
   *  surfaces this as the yellow "pin may not start cleanly" chip
   *  documented in TPN-4. */
  pinClampedFromOverlap?: boolean;
}

export interface PageMap {
  sections: PageMapEntry[];
  /** Highest endPage in the map — i.e. the total printed length. */
  totalPages: number;
}

/**
 * TX-2 (TPN-FOLLOWUP-PER-REPORT-PAGE-MAP-OVERRIDE, 2026-05-19) —
 * shape of a single per-section override stamped on a `SiteRagReport`.
 *
 *   - `startMode` (optional) overrides the template-stamped
 *     `SectionConfig.startMode`. Only honoured when present; absent
 *     leaves the template value untouched.
 *   - `pagePin`   (optional) overrides the template's `pagePin`.
 *     Honoured only when the resolved `startMode` is `'page-pin'`
 *     (matches the original resolver contract — see TPN-3 docs).
 *
 * Reproducibility invariant (HARD-RULE 9): omitting `pageMapOverrides`
 * entirely OR passing an empty object MUST produce a byte-identical
 * page map vs the template-only projection.
 */
export interface PageMapOverride {
  startMode?: ResolverSection['startMode'];
  pagePin?: number;
}

export interface ResolvePageMapOptions {
  /** Measured pages from the print-time DOM layout. Keyed by
   *  `sectionId`. Optional — when absent the resolver falls through to
   *  estimates. */
  measuredPages?: Record<string, number>;
  /** Page geometry passed to the estimator fallback. Defaults to A4. */
  estimatorOptions?: EstimatorOptions;
  /** Content snapshot resolver — when supplied the on-the-fly fallback
   *  uses live content instead of the canonical snapshot. */
  getContent?: (id: string) => EstimableSection | undefined;
  /**
   * TX-2 (2026-05-19) — per-report overrides. Keyed by `sectionId`.
   * When a key is present the override's `startMode`/`pagePin`
   * replace the section's template-stamped values BEFORE the resolver
   * loop runs. Absent keys leave the template projection intact —
   * pre-TX-2 reports (no `pageMapOverrides` at all) are byte-identical
   * to today's output. */
  pageMapOverrides?: Record<string, PageMapOverride>;
}

/**
 * Normalised view of a SectionConfig that the resolver works against.
 * Exposed as a type-export so test fixtures + the live caller can
 * build it without depending on the full SectionConfig surface.
 */
export interface ResolverSection {
  sectionId: string;
  visible?: boolean;
  order?: number;
  startMode?: SectionConfig['startMode'];
  pagePin?: number;
  estimatedPages?: number;
}

/**
 * TX-2 — merge a per-section override map over a list of resolver
 * sections. Returns a NEW array; never mutates the input. Override
 * keys that don't match any section are ignored (defensive — a
 * report that carries a stale override for a removed section still
 * resolves cleanly).
 *
 * Merge rules (per TX-2 spec):
 *   - `override.startMode` replaces `section.startMode` when defined.
 *     Setting `startMode: undefined` in an override is NOT a "clear"
 *     operation — `undefined` means "no opinion", template value
 *     wins.
 *   - `override.pagePin` replaces `section.pagePin` when defined.
 *     Same rule: `undefined` is "no opinion".
 *
 * Identity guard (HARD-RULE 9): when `overrides` is `undefined`,
 * `null`, OR an empty object, the function returns the INPUT array
 * unchanged (reference-equal). This keeps the no-override fast path
 * allocation-free and guarantees byte-identical resolver output for
 * archived reports.
 */
export function applyPageMapOverrides<T extends ResolverSection>(
  sections: T[],
  overrides: Record<string, PageMapOverride> | undefined | null,
): T[] {
  if (!overrides) return sections;
  const keys = Object.keys(overrides);
  if (keys.length === 0) return sections;
  let touched = false;
  const next = sections.map((s) => {
    const ov = overrides[s.sectionId];
    if (!ov) return s;
    // Skip when neither field carries a real value — avoids spurious
    // re-allocation when a caller stamps an empty override entry.
    const hasStartMode = ov.startMode !== undefined;
    const hasPagePin = ov.pagePin !== undefined;
    if (!hasStartMode && !hasPagePin) return s;
    touched = true;
    return {
      ...s,
      ...(hasStartMode ? { startMode: ov.startMode } : {}),
      ...(hasPagePin ? { pagePin: ov.pagePin } : {}),
    };
  });
  // If every override entry was a no-op, return the original array
  // for byte-identical reproducibility.
  return touched ? next : sections;
}

function sortSections<T extends ResolverSection>(sections: T[]): T[] {
  // Use `order` when ALL sections carry it (so we don't half-sort);
  // otherwise preserve the input array order (stable). We tag each
  // entry with its original index to break ties deterministically.
  const tagged = sections.map((s, idx) => ({ s, idx }));
  const allHaveOrder = tagged.every(t => typeof t.s.order === 'number');
  if (allHaveOrder) {
    tagged.sort((a, b) => {
      const ao = a.s.order ?? 0;
      const bo = b.s.order ?? 0;
      if (ao !== bo) return ao - bo;
      return a.idx - b.idx;
    });
  }
  return tagged.map(t => t.s);
}

/**
 * Compute the page count for a section, picking the best available
 * source. Pure helper — exported so unit tests can pin the resolution
 * order independently of the full resolve loop.
 */
export function resolveSectionPages(
  section: ResolverSection,
  opts: {
    measuredPages?: Record<string, number>;
    estimatorOptions?: EstimatorOptions;
    getContent?: (id: string) => EstimableSection | undefined;
  },
): { pages: number; source: PageMapSource } {
  // TPN-CORE (2026-05-19) — use Number.isFinite() so Infinity / NaN
  // from a malformed measuredPages map can't propagate into the page
  // cursor (which would yield Infinity totalPages and crash the TOC).
  const measured = opts.measuredPages?.[section.sectionId];
  if (typeof measured === 'number' && Number.isFinite(measured) && measured > 0) {
    return { pages: Math.max(1, Math.ceil(measured)), source: 'measured' };
  }
  if (
    typeof section.estimatedPages === 'number' &&
    Number.isFinite(section.estimatedPages) &&
    section.estimatedPages > 0
  ) {
    return { pages: Math.max(1, Math.ceil(section.estimatedPages)), source: 'estimated' };
  }
  const snapshot = opts.getContent?.(section.sectionId);
  if (snapshot) {
    return {
      pages: estimateSectionPages(snapshot, opts.estimatorOptions ?? DEFAULT_A4),
      source: 'estimated',
    };
  }
  // No snapshot resolver and no stamped estimate — fall back to a
  // single-page default. The Studio with a content resolver will
  // never hit this branch; isolated tests do.
  return { pages: 1, source: 'estimated' };
}

/**
 * Walk `sections` in order and produce a deterministic page map.
 *
 * The cursor invariant: `cursor` always points at the next AVAILABLE
 * page (i.e. one past the previous section's `endPage`). For 'auto'
 * and 'continue' modes the section's start equals the cursor; for
 * 'page-pin' the start is `Math.max(pin, cursor)` — pins can leap
 * forward (whitespace) but never backward (no overlap).
 *
 *   - 'continue' means "share the previous section's last page" → the
 *     section starts on `cursor − 1` (one page back) and consumes
 *     `pages` pages from there. Defensive minimum: a section that's
 *     the FIRST visible entry can't continue from anything, so the
 *     resolver downgrades 'continue' → 'auto' for index 0.
 *
 *   - 'page-pin' with no `pagePin` value is treated as 'auto' (the
 *     UI should never submit this state; defensive guard).
 */
export function resolvePageMap(
  sections: ResolverSection[],
  opts: ResolvePageMapOptions = {},
): PageMap {
  // TX-2 — apply per-report overrides BEFORE the visibility filter +
  // sort so an override-stamped startMode/pagePin participates in the
  // same cursor invariants as a template-stamped one. The helper
  // returns the input array unchanged when no overrides exist
  // (reference equality preserved → no GC pressure on the hot path).
  const merged = applyPageMapOverrides(sections, opts.pageMapOverrides);
  const visible = merged.filter(s => s.visible !== false);
  const ordered = sortSections(visible);
  const out: PageMapEntry[] = [];
  let cursor = 1;
  let priorEnd = 0;

  for (let i = 0; i < ordered.length; i++) {
    const s = ordered[i];
    const { pages, source: pageSource } = resolveSectionPages(s, {
      measuredPages: opts.measuredPages,
      estimatorOptions: opts.estimatorOptions,
      getContent: opts.getContent,
    });

    let startPage: number;
    let source: PageMapSource;
    let pinClampedFromOverlap = false;

    if (
      s.startMode === 'page-pin' &&
      typeof s.pagePin === 'number' &&
      Number.isFinite(s.pagePin) &&
      s.pagePin > 0
    ) {
      const desired = Math.floor(s.pagePin);
      if (desired < cursor) {
        // Pin would overlap the previous section — clamp forward.
        startPage = cursor;
        pinClampedFromOverlap = true;
        source = 'pinned';
      } else {
        startPage = desired;
        source = 'pinned';
      }
    } else if (s.startMode === 'continue' && i > 0 && priorEnd > 0) {
      // Share the previous section's last page (no break). Cursor
      // advance still happens off `endPage + 1` below.
      startPage = priorEnd;
      source = pageSource;
    } else {
      // 'auto' (and degraded 'continue' on the first section).
      startPage = cursor;
      source = pageSource;
    }

    const endPage = startPage + pages - 1;
    out.push({
      id: s.sectionId,
      startPage,
      endPage,
      source,
      ...(pinClampedFromOverlap ? { pinClampedFromOverlap: true } : {}),
    });

    cursor = endPage + 1;
    priorEnd = endPage;
  }

  const totalPages = out.length === 0 ? 0 : out[out.length - 1].endPage;
  return { sections: out, totalPages };
}

/**
 * Find an entry in a resolved page map by section id. Tiny helper
 * but exported because every UI consumer needs it and it lets the
 * resolver hide its internal storage representation.
 */
export function findPageMapEntry(map: PageMap, sectionId: string): PageMapEntry | undefined {
  return map.sections.find(e => e.id === sectionId);
}
